import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/redux/hooks';
import {
  clearFeaturedAction,
  setFeaturedAction,
  setSimpleField,
  swapFeaturedActions,
} from '../../../store/redux/slices/template.slice';
import {
  clearError,
  clearNamespaceErrors,
} from '../../../store/redux/slices/templateErrors.slice';
import {
  FeaturedAction,
  FeaturedActionSlotKey,
} from '../../../store/types/template';
import SectionWrapper from './SectionWrapper';
import OverrideRadio from './OverrideRadio';
import FeaturedActionSlots from './FeaturedActionSlots';
import { useTemplateFieldValidation } from './validation/useTemplateFieldValidation';
import { useEnhancedDesignForTemplate } from './hooks/useEnhancedDesignForTemplate';
import {
  createEmptyFeaturedActions,
  featuredActionsEqual,
} from './utils/featuredActionTypes';
import {
  APPLE_ENHANCED_DESIGN_FEATURED_ACTIONS_NAMESPACE as NAMESPACE,
  FEATURED_ACTIONS_SECTION_ID,
} from './utils/appleEnhancedDesignConstants';

const FeaturedActionsFields = ({ isEdit }: { isEdit: boolean }) => {
  const dispatch = useAppDispatch();
  const hook = useEnhancedDesignForTemplate();
  const templateType = useAppSelector((s) => s.template.templateType);
  const featuredActions = useAppSelector(
    (s) => s.template.appleEnhancedDesign.featuredActions
  );
  const onLoad = useAppSelector((s) => s.template._appleEnhancedDesignOnLoad);
  const overrideFeaturedActions = useAppSelector(
    (s) => s.template._overrideFeaturedActions
  );
  const errors = useAppSelector((s) => s.templateErrors);
  const { validateField, handleFocus, handleBlur } =
    useTemplateFieldValidation(NAMESPACE);

  const isGeneric = hook.storedDesign === 'generic';
  const slotsChanged = !featuredActionsEqual(
    featuredActions,
    onLoad.featuredActions ?? createEmptyFeaturedActions()
  );
  const turnedOn = isGeneric && onLoad.type !== 'generic';
  const showOverride = isEdit && isGeneric && (slotsChanged || turnedOn);

  const setOverride = (value: boolean) =>
    dispatch(setSimpleField({ field: '_overrideFeaturedActions', value }));

  useEffect(() => {
    if (!showOverride) {
      dispatch(
        setSimpleField({ field: '_overrideFeaturedActions', value: false })
      );
    }
  }, [showOverride, dispatch]);

  if (hook.storedDesign !== 'generic' || !hook.available) {
    return null;
  }

  const handleChange = (
    slot: FeaturedActionSlotKey,
    partial: Partial<FeaturedAction>
  ) => {
    dispatch(setFeaturedAction({ slot, value: partial }));
    const field = 'type' in partial ? 'type' : 'url';
    validateField(`${slot}.${field}`, partial[field] ?? null, {
      templateType,
      appleEnhancedDesign: {
        type: 'generic',
        featuredActions: {
          ...featuredActions,
          [slot]: { ...featuredActions[slot], ...partial },
        },
      },
    });
  };

  return (
    <SectionWrapper
      id={FEATURED_ACTIONS_SECTION_ID}
      title="Featured actions"
      tooltip="Tappable buttons shown below the pass in Apple Wallet, each linking out to a URL."
      subtitle="Add up to 2 tappable buttons shown below your pass on iOS 27."
      openOnLoad
    >
      <FeaturedActionSlots
        value={featuredActions}
        errors={errors}
        errorPrefix={NAMESPACE}
        onChange={handleChange}
        onRemove={(slot) => {
          dispatch(clearFeaturedAction(slot));
          dispatch(clearError(`${NAMESPACE}.${slot}.type`));
          dispatch(clearError(`${NAMESPACE}.${slot}.url`));
        }}
        onSwap={() => {
          dispatch(swapFeaturedActions());
          dispatch(clearNamespaceErrors(NAMESPACE));
        }}
        onFieldFocus={(path) => handleFocus(`${NAMESPACE}.${path}`)}
        onFieldBlur={handleBlur}
      />
      <OverrideRadio
        isVisible={showOverride}
        value={overrideFeaturedActions ? 'all' : 'new'}
        onChange={(value) => setOverride(value === 'all')}
        question="Who should these changes apply to?"
      />
    </SectionWrapper>
  );
};

export default FeaturedActionsFields;
